<template>
	<!-- SIDEBAR SMALL SCREEN -->
	<div class="lg:hidden relative sidebar-navigation print:hidden">
		<div class="absolute z-20 bg-base h-16 overflow-hidden">
			<button @click="toggleSideBarDrawer"
					class="lg:hidden px-4 h-16 border-r border-gray-200 text-base-content-light focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500">
				<span class="sr-only">Open sidebar</span>
				<Bars3BottomLeftIcon class="h-6 w-6"/>
			</button>
		</div>

		<div v-show="showSideBarDrawer" class="fixed flex z-40 inset-0  sidebar-navigation">
			<div class="fixed inset-0" aria-hidden="true">
				<div class="absolute inset-0 bg-gray-600 opacity-75"></div>
			</div>

			<div class="relative flex-1 flex flex-col max-w-xs w-full pt-4 pb-4 bg-base-100">
				<div class="absolute top-0 right-0 -mr-12 pt-2">
					<button @click="toggleSideBarDrawer"
							class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
						<span class="sr-only">Close sidebar</span>
						<XMarkIcon class="h-6 w-6 text-white"/>
					</button>
				</div>

				<div class="flex-shrink-0 flex items-center px-4">
					<a href="/">
						<img src="/smartsymbols-logo.png" class="block h-10 w-auto" alt="Smart Symbols home"/>
					</a>
				</div>

				<div class="mt-5 flex-1 h-0 overflow-y-auto">
					<nav class="px-2 space-y-1">
						<!-- Don't forget the desktop instance -->
						<SideBarMenu :classrooms="currentOrganisation?.classrooms" class="mb-12" />
					</nav>
				</div>

			</div>

			<div class="flex-shrink-0 w-14" aria-hidden="true">
				<!-- Dummy element to force sidebar to shrink to fit close icon -->
			</div>
		</div>
	</div>

	<!-- SIDEBAR DESKTOP -->
	<div class="hidden lg:flex lg:flex-shrink-0 h-dvh overflow-y-auto overflow-x-hidden">
		<div class="flex flex-col print:hidden transition-all" :class="[desktopSidebarCollapsed ? 'w-12' : 'w-64']">

			<div class="flex flex-col flex-grow border-r border-base-200 pt-3 pb-1 bg-base-100" style="box-shadow: inset -3px 0 6px -3px rgb(0 0 0 / 0.07);">

				<div class="flex gap-1 flex-shrink-0" :class="[desktopSidebarCollapsed ? 'flex-col items-center' : 'flex-row justify-between']">
					<a href="/" class="block">
						<img v-show="!desktopSidebarCollapsed" src="/smartsymbols-logo.png" :class="['ml-4 h-10 w-auto', desktopSidebarCollapsed ? 'hidden' : 'block']" alt="Smart Symbols home"/>
						<div :class="['w-10 h-10', desktopSidebarCollapsed ? 'block' : 'hidden']">
							<img
								v-show="desktopSidebarCollapsed"
								src="/smartsymbols-logomark.svg"
								alt="Smart Symbols home"
								class="w-full h-full object-cover scale-110"
							/>
						</div>
					</a>

					<TooltipRoot>
						<TooltipTrigger>
							<button type="button"
								class="hover:bg-base-200 rounded text-base-content-light hover:text-base-content px-2 py-2 transition-colors"
								@click="toggleDesktopSidebar">
								<!-- Phosphor icon: arrow-line-left -->
								<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 256 256" fill="currentColor" :class="[desktopSidebarCollapsed ? 'rotate-180' : '']">
									<path d="M232,128a8,8,0,0,1-8,8H91.31l58.35,58.34a8,8,0,0,1-11.32,11.32l-72-72a8,8,0,0,1,0-11.32l72-72a8,8,0,0,1,11.32,11.32L91.31,120H224A8,8,0,0,1,232,128ZM40,32a8,8,0,0,0-8,8V216a8,8,0,0,0,16,0V40A8,8,0,0,0,40,32Z"></path>
								</svg>
							</button>
						</TooltipTrigger>
						<TooltipPortal>
							<TooltipContent class="TooltipContent">
								{{ desktopSidebarCollapsed ? $t('Expand sidebar') : $t('Collapse sidebar') }}
								<TooltipArrow class="TooltipArrow" />
							</TooltipContent>
						</TooltipPortal>
					</TooltipRoot>

				</div>

				<div class="flex-grow flex flex-col" :class="[desktopSidebarCollapsed? '' : 'mt-2']">
					<nav class="flex-1 space-y-1" :class="desktopSidebarCollapsed? 'px-1' : 'px-1'">
						<!-- Don't forget the drawer instance -->
						<SideBarMenu :classrooms="currentOrganisation?.classrooms" :iconsOnly="desktopSidebarCollapsed" />
					</nav>
				</div>

				<div class="px-1 py-2 mt-8 flex flex-col" :class="[desktopSidebarCollapsed ? 'items-end' : 'items-stretch']">
					<span v-show="!desktopSidebarCollapsed" class="block ml-2 text-[0.6rem] text-base-content-light">v{{ version }}</span>
				</div>

			</div>
		</div>

	</div>
</template>

<script lang="ts">
import { Bars3BottomLeftIcon, XMarkIcon, ChevronDoubleLeftIcon } from '@heroicons/vue/24/outline'
import SideBarMenu from './SideBarMenu.vue'
import { useUsersStore } from "@/stores/Users.store";
import { mapState } from "pinia";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import AppConfig from "@/config/app.config";
import SideBarMenuLink from "@/components/layout/SideBarMenuLink.vue";
import SmaSyLocalStorage from "@/utils/SmaSyLocalStorage.util";
import { TooltipArrow, TooltipContent, TooltipPortal, TooltipProvider, TooltipRoot, TooltipTrigger } from 'reka-ui';

export default {
	name: "TheSidebar",
	components: {
		Bars3BottomLeftIcon,
		XMarkIcon, ChevronDoubleLeftIcon,
		SideBarMenu, SideBarMenuLink,
		TooltipArrow, TooltipContent, TooltipPortal, TooltipProvider, TooltipRoot, TooltipTrigger,
	},
	computed: {
		...mapState(useUsersStore, ['me']),
		...mapState(useOrganisationsStore, ['currentOrganisation']),
	},

	data() {
		return {
			showSideBarDrawer: false,
			desktopSidebarCollapsed: false,
			version: AppConfig.VERSION,
		}
	},

	beforeMount() {
		this.desktopSidebarCollapsed = SmaSyLocalStorage.getUserItem(this.me.id, 'desktop_sidebar_collapsed') ?? false;
	},

	methods: {
		toggleSideBarDrawer() {
			this.showSideBarDrawer = !this.showSideBarDrawer;
		},

		toggleDesktopSidebar() {
			this.desktopSidebarCollapsed = !this.desktopSidebarCollapsed;
			SmaSyLocalStorage.setUserItem(this.me.id, 'desktop_sidebar_collapsed', this.desktopSidebarCollapsed);

			// if current route is 'agenda', then reload the page to force the agenda to re-render as it doesn't fully
			// behave responsive to the sidebar width change. View component reload would be more subtle, but would also
			// require communication between unrelated components, so we just reload the page for now)
			if (this.$route.name === 'classroom.agenda') {
				// console.log('redirecting to ...');
				setTimeout(() => {
					window.location.reload();
				}, 200);
			}
		},
	}
}
</script>
